/**
 * 保管庫アイテムの「文字アバター」用の純関数群 (ookey-browser `favicon-char.ts` 準拠)。
 *
 * 実 API の `VaultItem` に色フィールドは無いため、名前 (URL の場合は domain) の
 * ハッシュで固定パレットから 1 色を決定的に選ぶ。配色は「淡い tint 背景 + 同系色
 * solid 文字」で、`.vault-item-avatar` CSS が形状 / 影 / サイズを担当する。
 */

/** URL 形式の name から domain 部分のみ取り出す (avatar 判定の前処理)。 */
const stripUrlAffixes = (value: string): string => {
  let v = value.replace(/^https?:\/\//iu, '').replace(/^www\./iu, '')
  const slashIndex = v.indexOf('/')
  if (slashIndex >= 0) v = v.slice(0, slashIndex)
  const queryIndex = v.indexOf('?')
  if (queryIndex >= 0) v = v.slice(0, queryIndex)

  return v === '' ? value : v
}

/**
 * 名前から表示するイニシャル (1〜2 文字) を返す。
 *   - 空 → '?'
 *   - 1 語目に大文字が 2 つ以上 (CamelCase) → 先頭 2 大文字を連結 (例: GitHub → 'GH')
 *   - それ以外 → 先頭 1 文字を大文字化 (例: notion → 'N'、保管庫 → '保')
 */
export const itemInitials = (name: string): string => {
  const trimmed = stripUrlAffixes(name.trim())
  if (trimmed === '') return '?'

  const firstWord = trimmed.split(/\s+/u)[0] ?? trimmed
  const caps = firstWord.match(/[A-Z]/gu) ?? []
  if (caps.length >= 2) return caps.slice(0, 2).join('')

  return firstWord.charAt(0).toUpperCase()
}

const AVATAR_COLORS_RGB = [
  '192,70,59', // red-brick
  '199,122,42', // orange
  '63,143,78', // green
  '59,115,196', // blue
  '91,83,192', // purple
  '184,68,126', // pink
  '47,143,138', // teal
  '122,106,46', // olive
] as const

/** 背景の透明度 (淡い tint)。 */
const AVATAR_BG_ALPHA = 0.16

export interface ItemAvatarPalette {
  /** アバター背景色 (淡い tint)。 */
  readonly bg: string
  /** 文字色 (同系色 solid)。 */
  readonly fg: string
}

/** name から決定的に bg + fg の組を返す (domain 部分をハッシュ化)。 */
export const itemAvatarPalette = (name: string): ItemAvatarPalette => {
  const trimmed = stripUrlAffixes(name.trim())
  let hash = 0
  for (let i = 0; i < trimmed.length; i += 1) {
    hash = (hash * 31 + trimmed.charCodeAt(i)) | 0
  }
  const rgb = AVATAR_COLORS_RGB[Math.abs(hash) % AVATAR_COLORS_RGB.length] ?? '59,115,196'

  return {
    bg: `rgba(${rgb}, ${AVATAR_BG_ALPHA})`,
    fg: `rgb(${rgb})`,
  }
}
